import { useState, useEffect, useRef, useCallback } from "react";

// ── Preferences (localStorage) ───────────────────────────────────────────────
const PREFS_KEY = "humanizeai_speech_prefs";
export function getSpeechPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; }
  catch { return {}; }
}
export function saveSpeechPrefs(updates) {
  const p = getSpeechPrefs();
  localStorage.setItem(PREFS_KEY, JSON.stringify({ ...p, ...updates }));
}

// ── Azure TTS config (CRA: REACT_APP_ prefix required) ──────────────────────
const AZURE_KEY    = process.env.REACT_APP_AZURE_TTS_KEY    || "";
const AZURE_REGION = process.env.REACT_APP_AZURE_TTS_REGION || "eastus";

export const azureAvailable = !!AZURE_KEY;

export const AZURE_VOICES = [
  { name: "en-US-JennyNeural",   label: "Jenny — US Female (Friendly)" },
  { name: "en-US-GuyNeural",     label: "Guy — US Male (Professional)" },
  { name: "en-US-AriaNeural",    label: "Aria — US Female (Natural)" },
  { name: "en-GB-SoniaNeural",   label: "Sonia — British Female" },
  { name: "en-AU-NatashaNeural", label: "Natasha — Australian Female" },
];

function escapeXml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function fetchAzureAudio(text, voiceName, rate = 1) {
  const rateAttr = rate === 1 ? "" : ` rate="${rate >= 1 ? "+" : ""}${Math.round((rate - 1) * 100)}%"`;
  const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">\
<voice name="${voiceName}"><prosody${rateAttr}>${escapeXml(text)}</prosody></voice></speak>`;
  const res = await fetch(
    `https://${AZURE_REGION}.tts.speech.microsoft.com/cognitiveservices/v1`,
    {
      method: "POST",
      headers: {
        "Ocp-Apim-Subscription-Key": AZURE_KEY,
        "Content-Type": "application/ssml+xml",
        "X-Microsoft-OutputFormat": "audio-24khz-48kbitrate-mono-mp3",
      },
      body: ssml,
    }
  );
  if (!res.ok) throw new Error(`Azure TTS ${res.status}`);
  return res.blob();
}

// ── Core hook ────────────────────────────────────────────────────────────────
export function useSpeech() {
  const [voices, setVoices]               = useState([]);
  const [speaking, setSpeaking]           = useState(false);
  const [paused, setPaused]               = useState(false);
  const [currentSentIdx, setCurrentSentIdx] = useState(-1);
  const [progress, setProgress]           = useState(0);

  const supported = typeof window !== "undefined" && !!window.speechSynthesis;

  const voicesRef      = useRef([]);
  const cancelRef      = useRef(false);
  const resolveRef     = useRef(null); // unblocks awaited Azure sentence Promise
  const audioRef       = useRef(null);
  const boundsRef      = useRef([]);

  // Load browser voices
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      if (v.length) { voicesRef.current = v; setVoices(v); }
    };
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, []);

  // Chrome bug: browser TTS silently stops after ~15 s
  useEffect(() => {
    if (!speaking || paused) return;
    if (getSpeechPrefs().engineMode === "azure") return;
    const id = setInterval(() => {
      if (window.speechSynthesis?.speaking && !window.speechSynthesis?.paused) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 14000);
    return () => clearInterval(id);
  }, [speaking, paused]);

  // ── stop ──────────────────────────────────────────────────────────────────
  const stop = useCallback(() => {
    cancelRef.current = true;
    resolveRef.current?.();   // unblock any awaited Azure sentence
    resolveRef.current = null;
    window.speechSynthesis?.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
    setPaused(false);
    setCurrentSentIdx(-1);
    setProgress(0);
  }, []);

  const pause = useCallback(() => {
    if (getSpeechPrefs().engineMode === "azure") {
      audioRef.current?.pause();
    } else {
      window.speechSynthesis?.pause();
    }
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    if (getSpeechPrefs().engineMode === "azure") {
      audioRef.current?.play?.();
    } else {
      window.speechSynthesis?.resume();
    }
    setPaused(false);
  }, []);

  // ── Browser TTS ────────────────────────────────────────────────────────────
  const speakBrowser = useCallback((sentences, rate, voiceName) => {
    window.speechSynthesis.cancel();
    cancelRef.current = false;
    setCurrentSentIdx(-1);
    setProgress(0);

    let fullText = "";
    const bounds = [];
    sentences.forEach((s, idx) => {
      const start = fullText.length;
      fullText += (s || "").trim() + " ";
      bounds.push({ start, end: fullText.length, idx });
    });
    boundsRef.current = bounds;
    const totalLen = fullText.length || 1;

    const utt = new SpeechSynthesisUtterance(fullText.trim());
    utt.rate = rate;
    if (voiceName) {
      const v = voicesRef.current.find(v => v.name === voiceName);
      if (v) utt.voice = v;
    }
    utt.onboundary = (e) => {
      const ci = e.charIndex;
      let sentIdx = 0;
      for (let i = bounds.length - 1; i >= 0; i--) {
        if (ci >= bounds[i].start) { sentIdx = bounds[i].idx; break; }
      }
      setCurrentSentIdx(sentIdx);
      setProgress(Math.min(99, Math.round((ci / totalLen) * 100)));
    };
    utt.onstart = () => { setSpeaking(true); setPaused(false); };
    utt.onend   = () => { setSpeaking(false); setPaused(false); setCurrentSentIdx(-1); setProgress(100); };
    utt.onerror = () => { setSpeaking(false); setPaused(false); };
    window.speechSynthesis.speak(utt);
  }, []);

  // ── Azure TTS (sentence by sentence for accurate highlighting) ─────────────
  const speakAzure = useCallback(async (sentences, rate, voiceName) => {
    cancelRef.current = false;
    setSpeaking(true);
    setPaused(false);
    setCurrentSentIdx(-1);
    setProgress(0);

    const total = sentences.length;
    for (let i = 0; i < total; i++) {
      if (cancelRef.current) break;
      setCurrentSentIdx(i);
      setProgress(Math.round((i / total) * 100));
      try {
        const blob = await fetchAzureAudio(sentences[i], voiceName, rate);
        if (cancelRef.current) break;
        const url = URL.createObjectURL(blob);
        await new Promise((resolve) => {
          resolveRef.current = resolve;
          const audio = new Audio(url);
          audioRef.current = audio;
          const done = () => { URL.revokeObjectURL(url); resolveRef.current = null; resolve(); };
          audio.onended = done;
          audio.onerror = done;
          audio.play().catch(done);
        });
      } catch {
        // skip failed sentence, continue
      }
    }
    if (!cancelRef.current) {
      setSpeaking(false);
      setCurrentSentIdx(-1);
      setProgress(100);
    }
  }, []);

  // ── Public: speak sentences array ─────────────────────────────────────────
  const speak = useCallback((sentences, rate = 1, voiceName = null) => {
    stop();
    const prefs = getSpeechPrefs();
    const useAzure = prefs.engineMode === "azure" && azureAvailable;
    setTimeout(() => {
      if (useAzure) {
        const voice = voiceName || AZURE_VOICES[0].name;
        speakAzure(sentences, rate, voice);
      } else {
        speakBrowser(sentences, rate, voiceName);
      }
    }, 60);
  }, [stop, speakBrowser, speakAzure]);

  // ── Public: speak plain text (no sentence tracking) ───────────────────────
  const speakText = useCallback((text, rate = 1, voiceName = null) => {
    stop();
    const prefs = getSpeechPrefs();
    const useAzure = prefs.engineMode === "azure" && azureAvailable;
    setTimeout(() => {
      if (useAzure) {
        const voice = voiceName || AZURE_VOICES[0].name;
        setSpeaking(true);
        fetchAzureAudio(text, voice, rate)
          .then(blob => {
            if (cancelRef.current) return;
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => { URL.revokeObjectURL(url); setSpeaking(false); };
            audio.onerror = () => { URL.revokeObjectURL(url); setSpeaking(false); };
            audio.play();
          })
          .catch(() => setSpeaking(false));
      } else {
        if (!window.speechSynthesis) return;
        setSpeaking(true);
        const utt = new SpeechSynthesisUtterance(text);
        utt.rate = rate;
        if (voiceName) {
          const v = voicesRef.current.find(v => v.name === voiceName);
          if (v) utt.voice = v;
        }
        utt.onend   = () => setSpeaking(false);
        utt.onerror = () => setSpeaking(false);
        window.speechSynthesis.speak(utt);
      }
    }, 60);
  }, [stop]);

  return {
    supported: supported || azureAvailable,
    browserSupported: supported,
    voices,
    speaking,
    paused,
    currentSentIdx,
    progress,
    speak,
    speakText,
    stop,
    pause,
    resume,
  };
}
